import type { AssignmentMap, CarrierGroup, ParsedProgram, SaleRow } from "./types";

// Extrae el código numérico del final del nombre del transportista.
//   "SAFARI L. SA (A)-BXN-759" → "759"
//   "TRANSPORTES ANDINOS - 12"  → "12"
//   "Sin código"                → ""
// El campo TRANSPORTISTA en el CSV de Numbers viene con el código como
// los últimos N dígitos contiguos. Si no hay dígitos al final, devuelve
// "" — el caller decide qué hacer (mostrar grupo "sin código" o
// descartarlo).
export function extractCarrierCode(transportista: string): string {
  const m = transportista.trimEnd().match(/(\d+)\s*$/);
  return m ? m[1] : "";
}

// Factores default del proxy peso/volumen. El admin puede pisarlos
// desde el panel de configuración (M10.5); se persisten en
// sessionStorage en optimizerConfig.ts.
export const DEFAULT_KG_PER_SOL = 0.1;
export const DEFAULT_M3_PER_SOL = 0.001;

// Peso simulado por venta (proxy). Se usa cuando el CSV no trae la
// columna `peso`. El factor es configurable en runtime.
export function simulatedWeightKg(valorVenta: number, kgPerSol: number = DEFAULT_KG_PER_SOL): number {
  return Math.round(valorVenta * kgPerSol * 10) / 10; // 1 decimal
}

// Volumen simulado por venta. Mismo razonamiento que el peso.
export function simulatedVolumeM3(valorVenta: number, m3PerSol: number = DEFAULT_M3_PER_SOL): number {
  return Math.round(valorVenta * m3PerSol * 1000) / 1000; // 3 decimales
}

// Resuelve el peso del pedido: usa el del CSV si vino, sino el proxy.
export function effectiveWeightKg(
  row: { valorVenta: number; pesoKg?: number },
  kgPerSol: number = DEFAULT_KG_PER_SOL,
): number {
  return row.pesoKg ?? simulatedWeightKg(row.valorVenta || 0, kgPerSol);
}

// Resuelve el volumen del pedido: usa el del CSV si vino, sino el proxy.
export function effectiveVolumeM3(
  row: { valorVenta: number; volumenM3?: number },
  m3PerSol: number = DEFAULT_M3_PER_SOL,
): number {
  return row.volumenM3 ?? simulatedVolumeM3(row.valorVenta || 0, m3PerSol);
}

// Paleta determinística por código de transportista — usamos el código
// como semilla con golden ratio para distribuir colores en el círculo
// HSL. Misma idea que `colorForVehicle` en el resto de la app: dos
// re-loads dan el mismo color al mismo chofer.
export function colorForCarrier(code: string): string {
  if (!code) return "#90A4AE"; // gris para "sin código"
  // hash simple: suma de dígitos del code para tener un número estable
  let seed = 0;
  for (let i = 0; i < code.length; i++) seed = seed * 31 + code.charCodeAt(i);
  const h = Math.abs(seed * 137.508) % 360;
  return `hsl(${h.toFixed(0)}, 70%, 50%)`;
}

// Devuelve el código del carrier al que pertenece la row, respetando
// el override de `assignments` si existe.
export function effectiveCarrierCode(row: SaleRow, assignments?: AssignmentMap): string {
  const override = assignments?.get(row.id);
  if (override !== undefined) return override;
  return extractCarrierCode(row.transportista);
}

// groupByCarrier toma las filas válidas del CSV (ya con lat/lon
// numéricos) y produce el agrupado consumido por la UI.
//
// `assignments` (opcional): map de overrides rowId → carrierCode. Las
// filas reasignadas migran al carrier destino y sus KPIs (sales, weight)
// se recalculan automáticamente sin tocar los datos originales.
//
// `factors` (opcional): factores de proxy peso/volumen cuando el CSV no
// trae esas columnas. Si las trae se ignoran. Vienen del panel de
// configuración (M10.5).
//
// Para carriers nuevos creados por reasignación (códigos que no
// existían en el CSV) el `name` queda como "Transportista <code>" —
// el caller puede sobreescribirlo si tiene un map de nombres.
export function groupByCarrier(
  rows: SaleRow[],
  assignments?: AssignmentMap,
  factors?: { kgPerSol?: number; m3PerSol?: number },
): ParsedProgram {
  const kgPerSol = factors?.kgPerSol ?? DEFAULT_KG_PER_SOL;
  const m3PerSol = factors?.m3PerSol ?? DEFAULT_M3_PER_SOL;
  const skippedRows = rows.filter((r) => !Number.isFinite(r.lat) || !Number.isFinite(r.lon));
  const validRows = rows.filter((r) => Number.isFinite(r.lat) && Number.isFinite(r.lon));

  // Pre-computamos los nombres originales por código — los carriers
  // que pierden todas sus rows por reasignación van a aparecer vacíos
  // pero el nombre se preserva para que el UI no salte.
  const originalNamesByCode = new Map<string, string>();
  for (const r of validRows) {
    const code = extractCarrierCode(r.transportista);
    if (!originalNamesByCode.has(code)) {
      originalNamesByCode.set(code, r.transportista);
    }
  }

  const byCode = new Map<string, CarrierGroup>();
  for (const r of validRows) {
    const code = effectiveCarrierCode(r, assignments);
    let g = byCode.get(code);
    if (!g) {
      g = {
        code,
        name: originalNamesByCode.get(code) ?? (code ? `Transportista ${code}` : "Sin código"),
        color: colorForCarrier(code),
        totalSales: 0,
        uniqueClients: 0,
        totalWeightKg: 0,
        totalVolumeM3: 0,
        rows: [],
      };
      byCode.set(code, g);
    }
    g.rows.push(r);
    g.totalSales += r.valorVenta || 0;
    g.totalWeightKg += effectiveWeightKg(r, kgPerSol);
    g.totalVolumeM3 += effectiveVolumeM3(r, m3PerSol);
  }
  // Calcular # clientes únicos por código.
  for (const g of byCode.values()) {
    const seen = new Set<string>();
    for (const r of g.rows) {
      const k = (r.codCliente || r.cliente).trim().toLowerCase();
      if (k) seen.add(k);
    }
    g.uniqueClients = seen.size;
    // Redondeo defensivo: muchas sumas chiquitas pueden dar floats
    // con ruido.
    g.totalWeightKg = Math.round(g.totalWeightKg * 10) / 10;
    g.totalVolumeM3 = Math.round(g.totalVolumeM3 * 1000) / 1000;
  }

  const carriers = Array.from(byCode.values()).sort((a, b) => {
    // Orden: con código primero (por código asc), "sin código" al final.
    if (a.code === "" && b.code !== "") return 1;
    if (a.code !== "" && b.code === "") return -1;
    return a.code.localeCompare(b.code, undefined, { numeric: true });
  });

  return {
    totalRows: validRows.length,
    skipped: skippedRows.length,
    carriers,
  };
}
